import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './PromisesBreaches.scss';

class PromisesBreaches extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            promises: [],
            stats: {
                total: 0,
                active: 0,
                fulfilled: 0,
                breached: 0,
                totalAmount: 0,
                breachedAmount: 0
            },
            filters: {
                status: null,
                startDate: null,
                endDate: null
            }
        };

        this.statusOptions = [
            { label: 'All', value: null },
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Fulfilled', value: 'FULFILLED' },
            { label: 'Breached', value: 'BREACHED' }
        ];
    }

    componentDidMount() {
        this.fetchPromises();
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchPromises = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });

        try {
            const { filters } = this.state;
            let url = `${baseUrlAdmin}/fee-decision/learning/promises?academicYear=${academicYear}`;
            
            if (filters.status) {
                url += `&status=${filters.status}`;
            }
            
            if (filters.startDate) {
                url += `&startDate=${filters.startDate.toISOString()}`;
            }
            
            if (filters.endDate) {
                url += `&endDate=${filters.endDate.toISOString()}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                const { promises, stats } = response.res.data;
                this.setState({
                    promises: promises || [],
                    stats: stats || this.state.stats
                });
            }
        } catch (error) {
            console.error('Error fetching promises:', error);
            this.showToast('error', 'Error', 'Failed to fetch promises data');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleFilterChange = (field, value) => {
        this.setState(prevState => ({
            filters: {
                ...prevState.filters,
                [field]: value
            }
        }), () => {
            this.fetchPromises();
        });
    }

    clearFilters = () => {
        this.setState({
            filters: {
                status: null,
                startDate: null,
                endDate: null
            }
        }, () => {
            this.fetchPromises();
        });
    }

    studentNameBodyTemplate = (rowData) => {
        return rowData.studentName || <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>;
    }

    admissionNoBodyTemplate = (rowData) => {
        return rowData.admissionNo || <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>;
    }

    classNameBodyTemplate = (rowData) => {
        return rowData.className || <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>;
    }

    statusBodyTemplate = (rowData) => {
        const statusMap = {
            'ACTIVE': { severity: 'info', label: 'Active' },
            'FULFILLED': { severity: 'success', label: 'Kept' },
            'BREACHED': { severity: 'danger', label: 'Breached' }
        };

        const status = statusMap[rowData.status] || { severity: 'info', label: rowData.status };
        return <Tag value={status.label} severity={status.severity} />;
    }

    dateBodyTemplate = (rowData) => {
        if (!rowData.promiseDate) return '-';
        const date = new Date(rowData.promiseDate);
        return date.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    }

    amountBodyTemplate = (rowData) => {
        const amount = rowData.amount || 0;
        return (
            <span style={{ fontFamily: 'InterMedium' }}>
                ₹{amount.toLocaleString('en-IN')}
            </span>
        );
    }

    daysOverdueTemplate = (rowData) => {
        if (rowData.status !== 'BREACHED') return '-';
        
        // Calculate days overdue from breach date or promise date
        const referenceDate = rowData.breachDate ? new Date(rowData.breachDate) : new Date(rowData.promiseDate);
        const today = new Date();
        const days = Math.floor((today - referenceDate) / (1000 * 60 * 60 * 24));
        
        if (days <= 0) return '-';
        
        return (
            <Tag value={`${days} days`} severity="danger" />
        );
    }

    render() {
        const { loading, promises, stats, filters } = this.state;

        if (loading && promises.length === 0) {
            return <LoadingComponent />;
        }

        const fulfillmentRate = stats.total > 0 
            ? ((stats.fulfilled / stats.total) * 100).toFixed(1)
            : 0;

        return (
            <div className="promises-breaches">
                <Toast ref={this.toast} position="bottom-right" />
                
                <Card title="📅 Promises & Breaches" className="p-mb-3">
                    <div className="p-grid p-mb-3">
                        <div className="p-col-12 p-md-3">
                            <div className="stat-card">
                                <div className="stat-label">Total Promises</div>
                                <div className="stat-value">{stats.total}</div>
                            </div>
                        </div>
                        <div className="p-col-12 p-md-3">
                            <div className="stat-card stat-success">
                                <div className="stat-label">Kept</div>
                                <div className="stat-value">{stats.fulfilled}</div>
                            </div>
                        </div>
                        <div className="p-col-12 p-md-3">
                            <div className="stat-card stat-danger">
                                <div className="stat-label">Breached</div>
                                <div className="stat-value">{stats.breached}</div>
                            </div>
                        </div>
                        <div className="p-col-12 p-md-3">
                            <div className="stat-card stat-info">
                                <div className="stat-label">Active</div>
                                <div className="stat-value">{stats.active}</div>
                            </div>
                        </div>
                    </div>

                    <Card className="fulfillment-card p-mb-3">
                        <div className="fulfillment-content">
                            <div className="fulfillment-label">
                                <i className="pi pi-chart-line" style={{ marginRight: '8px' }}></i>
                                Promise Fulfillment Rate
                            </div>
                            <div className="fulfillment-bar">
                                <ProgressBar 
                                    value={parseFloat(fulfillmentRate)} 
                                    displayValueTemplate={() => `${fulfillmentRate}%`}
                                    style={{
                                        height: '2.5rem',
                                        backgroundColor: '#e0e0e0'
                                    }}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card className="filter-card p-mb-3">
                    <div className="p-grid filter-section">
                        <div className="p-col-12 p-md-3">
                            <label>Status</label>
                            <Dropdown
                                value={filters.status}
                                options={this.statusOptions}
                                onChange={(e) => this.handleFilterChange('status', e.value)}
                                placeholder="All"
                                className="p-w-full"
                            />
                        </div>
                        <div className="p-col-12 p-md-3">
                            <label>From Date</label>
                            <Calendar
                                value={filters.startDate}
                                onChange={(e) => this.handleFilterChange('startDate', e.value)}
                                placeholder="Start Date"
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-w-full"
                            />
                        </div>
                        <div className="p-col-12 p-md-3">
                            <label>To Date</label>
                            <Calendar
                                value={filters.endDate}
                                onChange={(e) => this.handleFilterChange('endDate', e.value)}
                                placeholder="End Date"
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-w-full"
                            />
                        </div>
                        <div className="p-col-12 p-md-3">
                            <label style={{ visibility: 'hidden' }}>Actions</label>
                            <Button 
                                label="Clear Filters" 
                                icon="pi pi-filter-slash" 
                                className="p-button-outlined p-w-full"
                                onClick={this.clearFilters}
                            />
                        </div>
                    </div>
                    </Card>
                </Card>

                <Card title="📋 Promise Details" className="promise-table-card">
                    <DataTable
                        value={promises}
                        paginator
                        rows={20}
                        loading={loading}
                        emptyMessage="No promises found"
                        rowsPerPageOptions={[10, 20, 50]}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                        stripedRows
                    >
                        <Column 
                            field="studentName" 
                            header="Student" 
                            body={this.studentNameBodyTemplate}
                            sortable 
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            field="admissionNo" 
                            header="Admission No" 
                            body={this.admissionNoBodyTemplate}
                            sortable 
                            style={{ minWidth: '130px' }}
                        />
                        <Column 
                            field="className" 
                            header="Class" 
                            body={this.classNameBodyTemplate}
                            sortable 
                            style={{ minWidth: '100px' }}
                        />
                        <Column 
                            field="promiseDate" 
                            header="Promise Date" 
                            body={this.dateBodyTemplate}
                            sortable 
                            style={{ minWidth: '120px' }}
                        />
                        <Column 
                            field="amount" 
                            header="Amount" 
                            body={this.amountBodyTemplate}
                            sortable 
                            style={{ minWidth: '100px', textAlign: 'right' }}
                            headerStyle={{ textAlign: 'right' }}
                        />
                        <Column 
                            field="status" 
                            header="Status" 
                            body={this.statusBodyTemplate}
                            sortable 
                            style={{ minWidth: '100px' }}
                        />
                        <Column 
                            header="Days Overdue" 
                            body={this.daysOverdueTemplate}
                            style={{ minWidth: '130px' }}
                        />
                    </DataTable>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.currentUserPerms?.userInfo
});

export default connect(mapStateToProps)(withRouter(PromisesBreaches));
